import { tableV2TableSelectionReducer } from './state';
import type { TableV2TableOption, TableV2TableSelectionPersistedState, TableV2TableSelectionState } from './state';
import { computed, createModel, effect, signal, untracked } from '@preact/signals-react';
import type { ModelConstructor, ReadonlySignal } from '@preact/signals-react';
import { compareKey, parseKey, stringifyKey } from '@thinkalpha/table-client';
import type { Key } from '@thinkalpha/table-client';
import { TableClass } from '@thinkalpha/ui-types/contracts/workspace';
import type { ProxyClientService } from 'src/services/ProxyClientService';
import type { TableWidgetEvents, TableWidgetSignals } from 'src/types/EventBus';
import { EventBusBuilder } from 'src/types/EventBus/EventBus';
import type { EventBus } from 'src/types/EventBus/EventBus';

export type { TableV2TableOption, TableV2TableSelectionPersistedState } from './state';

export interface TableV2TableClassOption {
    label: string;
    value: TableClass | null;
}

const TABLE_CLASS_OPTIONS: readonly TableV2TableClassOption[] = [
    { label: 'None', value: null },
    { label: 'Platforms', value: TableClass.platforms },
    { label: 'Alpha Accounts', value: TableClass.joinedAccounts },
    { label: 'Accounts', value: TableClass.accounts },
    { label: 'Symbols', value: TableClass.symbols },
    { label: 'Traders', value: TableClass.traders },
    { label: 'Positions', value: TableClass.positions },
    { label: 'Alpha Positions', value: TableClass.joinedPositions },
    { label: 'No Positions', value: TableClass.missingPositions },
    { label: 'Fills', value: TableClass.fills },
    { label: 'Orders', value: TableClass.orders },
];

export function indexTableV2Keys(keys: readonly string[]): readonly TableV2TableOption[] {
    return keys
        .values()
        .unique()
        .map((value) => ({
            label: value,
            normalizedLabel: value.toLowerCase(),
            value,
        }))
        .toArray();
}

export function filterTableV2Keys(
    options: readonly TableV2TableOption[],
    rawQuery: string,
): readonly TableV2TableOption[] {
    const query = rawQuery.trim().toLowerCase();
    if (query === '') return options;

    const prefixMatches: TableV2TableOption[] = [];
    const substringMatches: TableV2TableOption[] = [];
    for (const option of options) {
        const matchIndex = option.normalizedLabel.indexOf(query);
        if (matchIndex === 0) {
            prefixMatches.push(option);
        } else if (matchIndex > 0) {
            substringMatches.push(option);
        }
    }
    return prefixMatches.concat(substringMatches);
}

export interface TableV2TableSelectionModelShape {
    readonly classOptions: ReadonlySignal<readonly TableV2TableClassOption[]>;
    readonly dirty: ReadonlySignal<boolean>;
    readonly eventBus: EventBus<TableWidgetSignals, TableWidgetEvents>;
    readonly filteredTableKeyOptions: ReadonlySignal<readonly TableV2TableOption[]>;
    readonly tableKeyResultSummary: ReadonlySignal<string>;
    readonly searchQuery: ReadonlySignal<string>;
    readonly selectedTableKey: ReadonlySignal<string | null>;
    readonly tableSettingsOpen: ReadonlySignal<boolean>;
    readonly table: ReadonlySignal<Key | null>;
    readonly tableClass: ReadonlySignal<TableClass | null>;
    readonly tableKeyOptions: ReadonlySignal<readonly TableV2TableOption[]>;
    markClean(): void;
    selectTableKey(value: string): void;
    setTableSettingsOpen(open: boolean): void;
    setSearchQuery(query: string): void;
    setTableClass(tableClass: TableClass | null): void;
    toJSON(): TableV2TableSelectionPersistedState;
}

export interface TableV2TableSelectionModelInit {
    persisted: TableV2TableSelectionPersistedState;
    proxyClientService: ProxyClientService;
}

export const TableV2TableSelectionModel: ModelConstructor<
    TableV2TableSelectionModelShape,
    [TableV2TableSelectionModelInit]
> = createModel<TableV2TableSelectionModelShape, [TableV2TableSelectionModelInit]>(
    (init: TableV2TableSelectionModelInit) => {
        const state = signal<TableV2TableSelectionState>({
            table: init.persisted.table,
            tableClass: init.persisted.class,
            tableKeyOptions: [],
            searchQuery: '',
            tableSettingsOpen: false,
        });
        const cleanPersisted = signal<TableV2TableSelectionPersistedState>(init.persisted);

        const dispatch = (action: Parameters<typeof tableV2TableSelectionReducer>[1]): void => {
            state.value = tableV2TableSelectionReducer(state.value, action);
        };

        let keysSubscription: { unsubscribe(): void } | undefined;
        effect(() => {
            const client = init.proxyClientService.target.value.client;
            untracked(() => {
                keysSubscription?.unsubscribe();
                dispatch({ type: 'SET_TABLE_KEY_OPTIONS', options: [] });
                keysSubscription = client.keys$.subscribe((keys) =>
                    dispatch({ type: 'SET_TABLE_KEY_OPTIONS', options: indexTableV2Keys(keys) }),
                );
            });
            return () => keysSubscription?.unsubscribe();
        });

        const table = computed(() => state.value.table);
        const tableClass = computed(() => state.value.tableClass);
        const selectedTableKey = computed(() => stringifyKey(table.value));
        const tableKeyOptions = computed(() => state.value.tableKeyOptions);
        const filteredTableKeyOptions = computed(() =>
            filterTableV2Keys(tableKeyOptions.value, state.value.searchQuery),
        );
        const persisted = computed<TableV2TableSelectionPersistedState>(() => ({
            table: table.value,
            class: tableClass.value,
        }));
        const dirty = computed(
            () =>
                !(persisted.value.table === null
                    ? cleanPersisted.value.table === null
                    : compareKey(persisted.value.table, cleanPersisted.value.table ?? undefined)) ||
                persisted.value.class !== cleanPersisted.value.class,
        );
        const tableKeys = computed(() => tableKeyOptions.value.map((option) => option.value));
        const tableSettingsOpen = computed(() => state.value.tableSettingsOpen);
        const searchQuery = computed(() => state.value.searchQuery);
        const tableKeyResultSummary = computed(() => {
            const matches = filteredTableKeyOptions.value.length;
            const total = tableKeyOptions.value.length;
            return matches === total
                ? `${total.toLocaleString()} tables`
                : `${matches.toLocaleString()} of ${total.toLocaleString()} tables`;
        });
        const classOptions = signal(TABLE_CLASS_OPTIONS);

        const selectTableKey = (value: string): void => dispatch({ type: 'SELECT_TABLE', table: parseKey(value) });
        const updateTableClass = (value: TableClass | null): void =>
            dispatch({ type: 'SET_TABLE_CLASS', tableClass: value });
        const updateTableSettingsOpen = (open: boolean): void => dispatch({ type: 'SET_TABLE_SETTINGS_OPEN', open });

        const eventBus = EventBusBuilder.create()
            .addEvent('widget::onTableKeySelect', ({ value }) => selectTableKey(value))
            .addEvent('widget::onTableClassSelect', ({ tableClass: value }) => updateTableClass(value))
            .addEvent('widget::setShowTableSettingsDialog', ({ show }) => updateTableSettingsOpen(show))
            .addSignal('widget::selectedTableKey', () => selectedTableKey)
            .addSignal('widget::showTableSettingsDialog', () => tableSettingsOpen)
            .addSignal('widget::tableKeys', () => tableKeys)
            .build();

        return {
            classOptions,
            dirty,
            eventBus,
            filteredTableKeyOptions,
            searchQuery,
            selectedTableKey,
            tableKeyResultSummary,
            tableSettingsOpen,
            table,
            tableClass,
            tableKeyOptions,
            markClean(): void {
                cleanPersisted.value = persisted.value;
            },
            selectTableKey,
            setTableSettingsOpen: updateTableSettingsOpen,
            setSearchQuery(query: string): void {
                dispatch({ type: 'SET_SEARCH_QUERY', query });
            },
            setTableClass: updateTableClass,
            toJSON(): TableV2TableSelectionPersistedState {
                return persisted.value;
            },
        };
    },
);

export type TableV2TableSelectionModel = InstanceType<typeof TableV2TableSelectionModel>;
